import { useEffect, useRef, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

function getWsUrl(path) {
  const base = API.replace(/^http/, 'ws');
  // Convert underscores to hyphens for WebSocket paths (e.g., room_actions -> room-actions)
  const wsPath = path.replace(/_/g, '-');
  return `${base}/api/ws/${wsPath}`;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const PING_INTERVAL = 25000;
const FALLBACK_POLL_INTERVAL = 8000;

export function useLiveFeed(channel, { pollFn }) {
  const [items, setItems] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const pingRef = useRef(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const itemsRef = useRef([]);

  // Keep itemsRef in sync
  useEffect(() => { itemsRef.current = items; }, [items]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const data = await pollFn();
        if (mountedRef.current) setItems(data);
      } catch {}
    }, FALLBACK_POLL_INTERVAL);
  }, [pollFn]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    try {
      const ws = new WebSocket(getWsUrl(channel));
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setConnected(true);
        retriesRef.current = 0;
        stopPolling();
        // Start ping keepalive
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        if (event.data === 'pong') return;
        try {
          const msg = JSON.parse(event.data);
          handleMessage(msg);
        } catch {}
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        cleanup();
        setConnected(false);
        startPolling();
        // Reconnect with backoff
        const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)];
        retriesRef.current += 1;
        setTimeout(() => { if (mountedRef.current) connect(); }, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      setConnected(false);
      startPolling();
      const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)];
      retriesRef.current += 1;
      setTimeout(() => { if (mountedRef.current) connect(); }, delay);
    }
  }, [channel, stopPolling, startPolling]);

  const handleMessage = useCallback((msg) => {
    if (channel === 'anonymous') {
      if (msg.type === 'init') {
        setItems(msg.posts || []);
      } else if (msg.type === 'new_post' && msg.post) {
        setItems(prev => {
          if (prev.some(p => p.id === msg.post.id)) return prev;
          return [msg.post, ...prev];
        });
      } else if (msg.type === 'update_post' && msg.post) {
        setItems(prev => prev.map(p => p.id === msg.post.id ? msg.post : p));
      } else if (msg.type === 'remove_post' && msg.post_id) {
        setItems(prev => prev.filter(p => p.id !== msg.post_id));
      }
    } else if (channel === 'music') {
      if (msg.type === 'init') {
        setItems(msg.requests || []);
      } else if (msg.type === 'new_request' && msg.request) {
        setItems(prev => {
          if (prev.some(r => r.id === msg.request.id)) return prev;
          return [msg.request, ...prev];
        });
      } else if (msg.type === 'update_request' && msg.request) {
        setItems(prev => {
          const exists = prev.some(r => r.id === msg.request.id);
          if (exists) return prev.map(r => r.id === msg.request.id ? msg.request : r);
          return [msg.request, ...prev];
        });
      } else if (msg.type === 'remove_request' && msg.request_id) {
        setItems(prev => prev.filter(r => r.id !== msg.request_id));
      }
    } else if (channel === 'room_actions') {
      if (msg.type === 'init') {
        setItems(msg.actions || []);
      } else if (msg.type === 'new_action' && msg.action) {
        setItems(prev => {
          if (prev.some(a => a.id === msg.action.id)) return prev;
          return [msg.action, ...prev];
        });
      } else if (msg.type === 'remove_action' && msg.action_id) {
        setItems(prev => prev.filter(a => a.id !== msg.action_id));
      }
    } else if (channel === 'spotlights') {
      if (msg.type === 'init') {
        setItems(msg.spotlights || []);
      } else if (msg.type === 'new_spotlight' && msg.spotlight) {
        setItems(prev => {
          if (prev.some(s => s.id === msg.spotlight.id)) return prev;
          return [msg.spotlight, ...prev];
        });
      } else if (msg.type === 'remove_spotlight' && msg.spotlight_id) {
        setItems(prev => prev.filter(s => s.id !== msg.spotlight_id));
      }
    }
  }, [channel]);

  const cleanup = useCallback(() => {
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      cleanup();
      stopPolling();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect, cleanup, stopPolling]);

  return { items, connected };
}
